/**
 * PipelineProjectService — the P2 project-controls reads for a converted project.
 *
 * Mirrors BidSheets/bidService.ts: a thin HttpService wrapper that owns the wire shapes, so the
 * screens hold typed data and never unwrap envelopes. Milestones are NOT here — the schedule tab
 * reads them through the existing `MilestoneService`, because P1 writes them through the existing
 * workflow engine and a parallel schedule route would give the same data two owners.
 *
 * Error semantics the screens rely on:
 *  - 403 on bid-vs-actual is a NORMAL answer, not a failure — it is how the server tells a
 *    financially-blind caller (or a non-member of the matter) that this panel is not for them.
 *    It is surfaced as `{ permitted: false }` rather than thrown, so the tab can render an explicit
 *    "not permitted" instead of an error state that reads like an outage.
 *  - 404 means the project does not exist for this tenant, and DOES throw.
 */
import HttpService, { type HttpError } from './HttpService';

const http = new HttpService();

/** One cost code's planned/actual/variance, server-computed. All money is strings (NUMERIC-safe). */
export interface BidVsActualRow {
  code: string;
  name: string;
  planned: string;
  actual: string;
  variance: string;
  variance_pct: string | null;
  /** Estimate-at-completion (gap review 2.3); null when the server computed none for the row. */
  projected: string | null;
  projected_variance: string | null;
}

/** One day of the BvA time axis (residual gap A1): cumulative planned/actual plus the day's
 * own estimate-at-completion — server-computed through the SAME eac() rule as the table's
 * projection, so the last day's `eac` IS `totals.projected`. The scrubber re-aims with these
 * figures and never recomputes them. */
export interface BidVsActualSeriesPoint {
  date: string;
  planned_cum: string;
  actual_cum: string;
  eac: string;
}

export interface BidVsActual {
  permitted: true;
  by_cost_code: BidVsActualRow[];
  totals: {
    planned: string; actual: string; variance: string; variance_pct: string | null;
    projected: string | null; projected_variance: string | null;
  };
  /** Internal cost with its estimate share DISCLOSED (Tier 4.3) — the whole route is 403 for
   * blind callers, so presence implies a financial caller. */
  cost?: {
    internal_total: string;
    recorded_total: string;
    estimated_total: string;
    estimate_share_pct: string | null;
    unknown_hours: string;
  };
  /** Cost meets contract (gap 2.7, completing 2.3's KPIs) — all null when no contract value. */
  margin?: {
    contract: string | null;
    fa_billable: string;
    projected_margin_pct: string | null;
    projected_margin_pct_net_fa: string | null;
    /** The gauge's target (A1): the server's effective_cost_margin ladder ×100 — a PERCENT
     * like its `_pct` siblings. null = no margin source anywhere; the gauge shows no band. */
    target_pct?: string | null;
  };
  /** Project-wide percent complete (produced ÷ distance, FA excluded); null when no distance. */
  percent_complete: string | null;
  /** The A1 daily series — one row per calendar day, min(first ticket, planned start) to
   * max(planned end, last ticket). Absent on cached pre-A1 payloads; empty = no time axis yet. */
  series?: BidVsActualSeriesPoint[];
  /** How the planned line was drawn: 'dated' per phase windows, or 'flat' at total because NO
   * phase carries dates — disclosed rather than inventing dates (the plan's named hazard). */
  planned_curve?: 'dated' | 'flat';
  /** Subcontracted scope (P4): REPORTED, never measured. Its planned dollars are real, but no
   * actuals are tracked against a sub's own crew, so folding it into variance would flatter every
   * subbed job — planned money with nothing to offset it reads as "under budget" when the honest
   * statement is "not our cost to incur". Optional: older payloads render unchanged without it. */
  subcontract?: {
    planned: string;
    lines: number;
    actuals_tracked: false;
  } | null;
  /** Which frozen scope these figures were measured against, and how many exist (P4). `latest > 1`
   * is the only condition under which choosing a version is a real choice. */
  baseline?: {
    selected: number;
    latest: number;
  } | null;
}

export interface BidVsActualDenied {
  permitted: false;
}

/** One P&L day (Tier 4.2): three NAMED revenue lines, the 4.3 cost picture at day grain.
 * `overhead` is ABSENT (null) until a rate resolves — the source bid's own, else the company
 * default (Tier 4.1 rider; see `Pnl.overhead_on_revenue_source`) — and 0.00 with a resolved rate
 * of 0 is a DECISION, not an absence. `net` is WITHHELD (null) when the day carries uncosted
 * hours — no confident net over an unknown. */
export interface PnlDay {
  date: string;
  work_at_charge_out: string;
  fa_billable: string;
  invoice_cost_plus: string;
  revenue: string;
  cost_recorded: string;
  cost_estimated: string;
  invoices_cost: string;
  cost: string;
  gross: string;
  overhead: string | null;
  net: string | null;
  unknown_hours: string;
}

export interface Pnl {
  permitted: true;
  days: PnlDay[];
  totals: PnlDay;
  overhead_configured: boolean;
  cost_plus_configured: boolean;
  /** The corporate-overhead-on-revenue percentage the server applied, and WHICH rung set it
   * (Tier 4.1 rider, 2026-08-08): 'bid' = the source bid's own figure, seeded at its creation
   * from the company default and never repriced by a later change to that default; 'global' =
   * the company default answering for a bid that never captured one. Both null when neither
   * rung resolves — and "0" with a source is a DECISION, not an absence. Optional: the panel
   * renders unchanged without them. */
  overhead_on_revenue_pct?: string | null;
  overhead_on_revenue_source?: 'bid' | 'global' | null;
  /** Change-order value requested but not yet agreed (P4). NOT part of revenue or cost: an
   * approved change order becomes scope and is measured as planned, so folding exposure into
   * either would double-count it the moment it is agreed. `statuses` is carried so the panel can
   * say what the figure counted instead of leaving a PM to guess. Optional: older payloads render
   * unchanged without it. */
  change_order_exposure?: {
    value: string;
    change_orders: number;
    statuses: string[];
  } | null;
}

export interface PnlDenied {
  permitted: false;
}

/** Derived by the SERVER — the one sanctioned use of the entered actual dates (0028): complete
 * on actual_end; in progress on actual_start OR any day sheet on the phase; else future. These
 * are the phase's WORDS; the Gantt keeps its own bar paint and the two may differ. */
export type PhaseLifecycleStatus = 'future' | 'in_progress' | 'complete';

export interface PhaseCostCodeRef {
  code: string;
  name: string;
  /** Client-facing code (e.g. "106") when set on the catalog — shown in the Gantt when present. */
  external_code?: string | null;
}

export interface ProjectPhase {
  id: string;
  name: string;
  sequence: number;
  distance_m: string;
  production_per_day_m: string;
  planned_start: string | null;
  planned_end: string | null;
  /** Entered by the PM (0028) — reality's own columns; the planned window above stays frozen. */
  actual_start: string | null;
  actual_end: string | null;
  /** Sheet EVIDENCE (first/last day-sheet date on this phase). Rendered beside the entered
   * actuals; when they disagree both show and neither wins silently. */
  first_sheet_date: string | null;
  last_sheet_date: string | null;
  status: PhaseLifecycleStatus;
  /** Distinct cost codes on this phase's frozen baseline lines — from GET /phases. */
  cost_codes?: PhaseCostCodeRef[];
}

export interface ProjectCostCode {
  id: string;
  code: string;
  name: string;
  unit_driver: string | null;
  on_gantt: boolean;
  is_burn_rate: boolean;
  is_force_account: boolean;
  /** The bid's crew counts per code — hands and units (quantities; not stripped). */
  planned_manpower: string | null;
  planned_equipment: string | null;
  /** Absent entirely for a financially-blind caller — stripped server-side, never sent as 0. */
  planned_dollars?: string | null;
}

/** One labour/equipment line on a field or FA ticket. Rate keys are ABSENT for blind callers —
 * stripped server-side, never sent as 0. */
export interface TicketLine {
  /** The line's IDENTITY, on BOTH ticket kinds and for two different reasons.
   *
   * On a FORCE-ACCOUNT line (blocker B9) it is what lets an edit preserve anything at all: without
   * it the replace path delete-reinserted from a payload with no handle, wiping
   * `source_timesheet_line_id`, `release_status`, `released_by` and `released_at` on every save —
   * un-releasing rows a PM had approved. On a FIELD-TICKET line it is what lets a client NAME a
   * line: the office basis override and the off-baseline disposition both take one. Nothing was
   * minted in either case; the reads simply never returned it. A FACT, never stripped. */
  id?: string;
  kind: 'labour' | 'equipment';
  description: string;
  /** The catalog picks (residual gap A3's load-bearing read): identity FACTS like
   * inventory_item_id — never stripped; the edit form repopulates its pickers from these
   * (without them every line's pick would silently drop — the named failure). */
  labour_role_id?: string | null;
  equipment_item_id?: string | null;
  /** Line-level cost code (gap review 2.9) — null inherits the ticket's header code. */
  project_cost_code_id?: string | null;
  qty: string;
  hours: string;
  /** The day sheet's payroll split (gap 3.1) — null = not split; OT = hours − reg − trav. */
  reg_hours?: string | null;
  trav_hours?: string | null;
  /** Per person per day (gap 3.2) — the Long Sheet's weekly Sub/Tru counts. */
  subsistence?: boolean;
  truck?: boolean;
  /** Equipment unit tracking (gap 3.3): the fleet unit this line IS (inventory_items), its
   * registry identity joined on, and the day's hour-meter readings. Identity + hours FACTS —
   * they survive stripping, and the Equip Summary rolls up from exactly these. */
  inventory_item_id?: string | null;
  unit_tag?: string | null;
  unit_name?: string | null;
  meter_start?: string | null;
  meter_end?: string | null;
  /** P7b, force-account lines only: the release gate the commercial sweep created (`held` until a
   * PM acts) and the frozen source pointer. A line carrying a source pointer is NOT the author's
   * to re-price — the form renders it read-only, or it ships an editable field whose save 400s. */
  release_status?: string | null;
  released_by?: string | null;
  released_at?: string | null;
  source_timesheet_line_id?: string | null;
  rate?: string;
  line_total?: string;
  fa_rate?: string;
  /** Which rung priced an FA line's charge-out (P2 Build M): 'org_msa' (the matter's client
   * organization's MSA card) or 'tenant_default' (the catalog). A provenance FACT like
   * cost_source — survives stripping, never carries a figure. Null on pre-0040 rows written
   * during a split rollout; absent on field-ticket lines (bid-scope has no org rung). */
  rate_source?: string | null;
  /** Internal cost (Tier 4.3): frozen at write, absent for blind callers; cost_source is the
   * provenance FACT ('unit' | 'catalog' | 'estimated') and survives — never carrying a figure. */
  cost_rate?: string | null;
  cost_total?: string | null;
  cost_source?: string | null;
  /** WHICH rate_schedule row priced this line (P5 flip 1, migration 0047 §3). Unlike the two
   * *_source fields above it STRIPS for a blind caller: those name a rung from a closed set,
   * while a row id is an exact equivalence class over the amount. Null also means "no row priced
   * it", which covers three cases — the catalog fallback fired, the line names no catalog
   * subject, or the row predates 0047. Nothing in the UI reads it today; it exists so the flip
   * is observable over HTTP. */
  rate_schedule_id?: string | null;
  /** P7a (0049) — whose hours these are. Absent on a legacy crew line, which reads exactly as it
   *  did before: the fan-out is additive, not a replacement. On the FORCE-ACCOUNT read this was a
   *  SILENT DROP until P7b: the read never returned it, so a wholesale edit erased the person. */
  employeeid?: string | null;
  /** Display name only. The line read projects id + name and never spreads the employees row —
   *  `billedrate` is a rate-table id that neither money guard can see (finding A37). */
  employee_name?: string | null;
  /** The person-day this line belongs to (0049). */
  timesheet_id?: string | null;
  /** P7b (0050) — the force-account reason entered WITH the line, and the AUTHORITATIVE billing
   *  basis written at approval beside the entered one below. Both halves of the basis pair
   *  persist, which is what makes the reclassification report's disagreement dimension possible;
   *  `billing_basis` is null until the person-day is approved. `is_off_baseline` and
   *  `disposition` are the queue's, carried here so the edit form and the queue read one
   *  payload. All four are FACTS and survive stripping. */
  fa_reason_code?: string | null;
  billing_basis?: string | null;
  is_off_baseline?: boolean;
  disposition?: string | null;
  /** WHICH LAW judged this line, snapshotted from the line's OWN site at approval. Null until the
   *  person-day is approved. Per line rather than per header, because a person can work two sites
   *  in a day and the schema deliberately preserves that disagreement. */
  jurisdiction_id?: string | null;
  /** P7b (R4): WHEN this line's person-day was approved, or null. A workflow FACT — the approval
   *  panel filters its capped oldest-first window on it, so approved days stop occupying the 25
   *  slots that exist to surface unapproved ones. */
  classified_at?: string | null;
  /** The ENTERED axis: `working` | `travel`. Its verified counterpart is P7b's and does not
   *  exist yet — nothing is classified in this phase. */
  hour_type_entered?: string | null;
  /** Proposed at entry, supervisor-overridable (d.72). The authoritative half is P7b's. */
  billing_basis_field?: string | null;
  /** Where the labour happened — what picks the law when P7b runs (d.14). */
  site_id?: string | null;
}

export interface FieldTicket {
  id: string;
  ticket_date: string;
  project_phase_id: string | null;
  project_cost_code_id: string | null;
  cost_code: string | null;
  weather: string | null;
  /** Structured range beside the free-text weather (gap 3.6); null = unrecorded. */
  temp_high_c?: string | null;
  temp_low_c?: string | null;
  /** The two signature lines (gap 3.5) — typed attestation names. */
  contractor_rep?: string | null;
  customer_rep?: string | null;
  /** The day sheet's third-party block (gap 3.4); invoice_value strips for blind readers. */
  invoices?: {
    po_number: string | null;
    vendor: string;
    description: string | null;
    invoice_value?: string;
  }[];
  production_m: string;
  man_hours: string;
  equipment_hours: string;
  is_force_account: boolean;
  reason_code: string | null;
  /** The A8 miss pair (0023): why a bid-scope day fell below its phase's planned rate.
   * Facts, not money — never stripped; null = no miss recorded (pre-0023 rows too). */
  production_miss_reason?: string | null;
  production_miss_note?: string | null;
  notes: string | null;
  lines: TicketLine[];
}

/** What an author SENDS — no rate anywhere; the server resolves and freezes prices. */
export interface CreateTicketLine {
  /** THE LINE'S IDENTITY on an edit (R3 — B9's FA treatment carried to field lines). ABSENT
   * means a NEW line, which is what create sends for every line; present means "edit the row
   * the ticket read returned", which is what lets the replace preserve the judgement columns
   * the author does not supply — the off-baseline note, the disposition, and the office's
   * authoritative billing basis. An id the caller did not get from this ticket is REFUSED. */
  id?: string;
  kind: 'labour' | 'equipment';
  description?: string;
  labour_role_id?: string;
  equipment_item_id?: string;
  /** Optional split-day code (gap review 2.9); omitted = the ticket's header code. */
  project_cost_code_id?: string | null;
  qty?: number;
  hours?: number;
  reg_hours?: number;
  trav_hours?: number;
  subsistence?: boolean;
  truck?: boolean;
  /** Equipment unit tracking (gap 3.3) — equipment-only, qty must be 1 with a unit link. */
  inventory_item_id?: string;
  meter_start?: number;
  meter_end?: number;
  /** P7a: the person whose hours these are. When set, the server requires `qty: 1` and
   *  `kind: 'labour'` and attaches (or creates) that person's day — omit it for a legacy crew
   *  line, which still writes exactly as before. */
  employeeid?: string;
  /** `working` | `travel`. Labour lines only. */
  hour_type_entered?: string;
  billing_basis_field?: string;
  site_id?: string;
  /** P7b (0050): WHY these hours are force account. It pairs with `billing_basis_field` above and
   * NOT with the authoritative basis, which is not written until approval. Required whenever the
   * basis is `force_account`, and refused on an equipment line — the two 0050 CHECKs together
   * close the equipment force-account route. */
  fa_reason_code?: string;
}

/** What a FORCE-ACCOUNT author sends, which is NOT `CreateTicketLine` (blocker B9).
 *
 * The split predates R3: it was minted when only force account carried a line identity. Both
 * replaces upsert on `id` now, but the shapes stay separate because everything else about them
 * differs — the field line's payroll split, unit tracking and entered axis have no FA
 * counterpart. */
export interface CreateForceAccountLine {
  /** ABSENT means a NEW line, which is what create sends for every line; present means "edit the
   * row I was given". An id the caller did not get from this ticket is REFUSED, not ignored. */
  id?: string;
  kind: 'labour' | 'equipment';
  description?: string;
  labour_role_id?: string;
  equipment_item_id?: string;
  qty?: number;
  hours?: number;
  employeeid?: string;
}

export interface CreateFieldTicketBody {
  ticket_date: string;
  project_phase_id?: string | null;
  project_cost_code_id?: string | null;
  weather?: string;
  temp_high_c?: number;
  temp_low_c?: number;
  production_m?: number;
  man_hours?: number;
  equipment_hours?: number;
  is_force_account?: boolean;
  reason_code?: string;
  /** The A8 miss pair, day-sum grained (the validation set): a reason is DEMANDED when the
   * (date, phase)'s non-FA sum misses the rated plan, PERMITTED while this ticket is short
   * on its own (a cured split ticket keeps its entry-time reason), refused on a ticket
   * at/above the rate — and `other` always owes the note. */
  production_miss_reason?: string;
  production_miss_note?: string;
  notes?: string;
  /** The two signature lines (gap 3.5) — typed attestation names. */
  contractor_rep?: string;
  customer_rep?: string;
  lines: CreateTicketLine[];
  /** Third-party invoices (gap 3.4). invoice_value is the RECORDED doctrine exception:
   * author-supplied money off the vendor's paper — the one money key in this body. */
  invoices?: {
    po_number?: string;
    vendor: string;
    description?: string;
    invoice_value: number;
  }[];
}

export interface ForceAccountTicket {
  id: string;
  fa_date: string;
  reason_code: string;
  billable: boolean;
  description: string;
  notes: string | null;
  lines: TicketLine[];
}

/** What an FA author SENDS — hours and a reason, never a rate or `billable` (the server
 * prices from the MSA card and freezes billability). One body, two verbs (fa-ticket-edit):
 * create posts it, the edit PUTs the same shape. */
export interface CreateForceAccountBody {
  fa_date: string;
  reason_code: string;
  description: string;
  notes?: string;
  lines: CreateForceAccountLine[];
}

/** One (person, day, hour_type) sum from `classified_blocks`. Hours and a date — no money. */
export interface ClassifiedHour {
  employeeid: string;
  day: string;
  hour_type: 'regular' | 'overtime' | 'double_time';
  hours: string;
}

export type OffBaselineDisposition = 'absorbed' | 'force_account' | 'change_order';

/** A line whose `(phase, cost_code)` pair was not on the baseline version in force when it was
 * written. The judgement is a POINT IN TIME one: a baseline approved afterwards neither flags nor
 * unflags anything already recorded. */
export interface OffBaselineLine {
  id: string;
  ticket_date: string;
  description: string;
  kind: 'labour' | 'equipment';
  qty: string;
  hours: string;
  phase: string | null;
  cost_code: string | null;
  employeeid: string | null;
  employee_name: string | null;
  note: string | null;
  disposition: OffBaselineDisposition | null;
  disposition_by: string | null;
  disposition_at: string | null;
}

/** Two dimensions, and only one of them has data. `available: false` carries the REASON, and the
 * client must render the reason — an empty table reads as agreement, which is the failure this
 * report exists to prevent. */
export interface ReclassificationReport {
  matterid: string;
  window: { start: string | null; end: string | null };
  basis_disagreement: {
    available: boolean;
    reason: string | null;
    considered: number;
    disagreed: number;
    /** NOT `rate`: that key is in `MONEY_KEYS`, so naming it would strip the report's headline
     *  number for the superintendent audience this surface targets. Nullable because a rate with
     *  no denominator is UNDEFINED — rendering it as zero would say the field and the office
     *  agreed, which is the misreading the empty-dimension design exists to prevent. */
    disagreement_rate: string | null;
    by_supervisor: {
      supervisor: string | null; matterid: string;
      considered: number; disagreed: number; disagreement_rate: string | null;
    }[];
  };
  entered_vs_statutory: {
    available: boolean;
    reason: string | null;
    claim_lines: number;
    with_residual: number;
    statutory_premium_lines?: number;
    disagreed?: number;
  };
  /** The condition attached to stripping `ruleset_id`: a blind reader gets the FACT and (for a
   * published baseline amendment) the DATE of a ruleset change, never the row id. An OVERRIDE's
   * date is withheld — it matches no published amendment, so it would disclose the private legal
   * position by arithmetic. */
  ruleset_provenance: {
    rules_applied: number;
    changed_within_window: boolean;
    any_override_applied: boolean;
    override_dates_withheld: boolean;
    entries: {
      jurisdiction: string; source: 'baseline' | 'override';
      effective_from: string | null; blocks: number; hours: string;
    }[];
  };
}

export interface CascadePhase {
  phase_id: string;
  phase: string;
  sequence: number;
  fa_days: number;
  days_pushed: number;
  daily_standby: string | null;
  standby_cost: string | null;
}

export interface ForceAccountSummary {
  permitted: true;
  totals: { total: string; billable: string; absorbed: string };
  by_reason: { reason_code: string; billable: boolean; total: string }[];
  /** The knock-on cascade (gap 2.7): what the recorded FA days push downstream. */
  cascade?: {
    phases: CascadePhase[];
    totals: { recorded_fa_days: number; daily_standby_exposure: string; standby_cost: string };
  };
}

export interface ChangeOrderLine {
  description: string;
  amount: string;
  included: boolean;
  sequence: number;
  /**
   * Optional attribution (P4). Null is a real answer, not a missing field: a "general disruption"
   * line has no honest cost code, and the variance reports it as UNCODED rather than guessing.
   */
  project_cost_code_id: string | null;
  project_phase_id: string | null;
}

export interface ChangeOrder {
  id: string;
  title: string;
  description: string | null;
  status: string;
  created_at: string;
  requested_value: string;
  /** The baseline version this CO froze on approval; null if it never froze one. Non-null means
   * the server's replace guard refuses line rewrites INDEPENDENT of status — an Approved CO is
   * already locked. Optional: older payloads and fixtures render unchanged without it. */
  baseline_version?: number | null;
  lines: ChangeOrderLine[];
}

export interface ChangeOrders {
  permitted: true;
  change_orders: ChangeOrder[];
}

export interface ChangeOrderHistoryEntry {
  action: string;
  from_status: string | null;
  to_status: string | null;
  performed_by: string | null;
  performed_at: string;
  notes: string | null;
}

/** One phase's metres/hours rollup from the money-free `/production` route (A6). Phases-first:
 * a phase with baseline labour and no tickets yet still gets a row, so the planned denominator
 * never understates on a just-started job. */
export interface ProductionRow {
  phase: string;
  planned_production_per_day_m: string | null;
  produced_m: string;
  fa_produced_m: string;
  man_hours: string;
  equipment_hours: string;
  tickets: number;
  /** Labour-only Σ qty×hrs×days over the frozen baseline — a quantity, stripping-safe. */
  planned_man_hours: string;
}

export interface CrewLoadingRow {
  phase_id: string | null;
  phase: string;
  planned_headcount: string;
  actual_headcount: string;
  man_hours: string;
}

export interface EquipmentLoadingRow {
  phase_id: string | null;
  phase: string;
  planned_units: string;
  actual_units: string;
  equipment_hours: string;
  /** Bid-scope completion (produced ÷ distance, FA excluded) — the demob nudge's factual half. */
  percent_complete: string | null;
}

export interface CrewLoading {
  as_of_date: string | null;
  crews: CrewLoadingRow[];
  equipment: EquipmentLoadingRow[];
}

export interface VerifiedDay {
  day: string;
  verified_by: string | null;
  verified_at: string;
}

/** One phase-crew assignment. Money-free by construction — names, ids and timestamps,
 * never a rate. `on_team` is false for a person REMOVED from the matter team after assignment: the
 * read discloses the stranded row rather than dropping it, so the PM can see and clear it.
 * `role_key` (0026) names the planned slot group the person fills; null is the Tier-1 role-less
 * assignment. */
export interface PhaseCrewMember {
  project_phase_id: string;
  employeeid: string;
  name: string | null;
  email: string | null;
  assigned_by: string | null;
  assigned_at: string | null;
  on_team: boolean;
  role_key: string | null;
}

/** One planned role slot group (0026): what the frozen baseline priced for this phase. `role_key`
 * is a labour_roles id when any line resolved the catalog, else `label:` + the normalised label —
 * a catalog "Welder" and a hand-typed "welder " are ONE group. `planned` is hands, never money. */
export interface PhaseCrewSlot {
  project_phase_id: string;
  role_key: string;
  label: string;
  planned: number;
}

export interface PhaseCrewRead {
  assignments: PhaseCrewMember[];
  slots: PhaseCrewSlot[];
}

/** One phase-equipment assignment (0027) — the crew row's unit-for-person mirror. Money-free by
 * construction. `unit_active` is false for a unit RETIRED after assignment: the read discloses the
 * stranded row rather than dropping it. `unit_equipment_item_id` is the unit's catalog type — the
 * panel discloses a substitution (unit type ≠ slot key), never refuses one. `sheet_count` is
 * "seen on N day sheets on this phase" (0020 evidence); the panel renders nothing at 0. */
export interface PhaseEquipmentUnit {
  project_phase_id: string;
  inventory_item_id: string;
  name: string | null;
  asset_tag: string | null;
  unit_active: boolean;
  unit_equipment_item_id: string | null;
  assigned_by: string | null;
  assigned_at: string | null;
  equipment_key: string | null;
  sheet_count: number;
}

/** One planned equipment slot group (0027): what the frozen baseline priced for this phase.
 * `equipment_key` is an equipment_catalog id when any line resolved the catalog, else `label:` +
 * the normalised label — a catalog "Trackhoe 350" and a hand-typed "trackhoe 350 " are ONE group.
 * `planned` is units, never money. */
export interface PhaseEquipmentSlot {
  project_phase_id: string;
  equipment_key: string;
  label: string;
  planned: number;
}

/** A TARGET-LESS type slot from the frozen load matrix (0030, Build T): the mob/demob TYPE
 * axis. No `planned` by design — the matrix counts LOADS, not units, so a quota would lie;
 * the loads render as muted disclosure beside a plain "n named". */
export interface PhaseEquipmentTypeSlot {
  project_phase_id: string;
  equipment_key: string;
  label: string;
  loads: { heavy: number; legal: number; pickup: number };
}

export interface PhaseEquipmentRead {
  assignments: PhaseEquipmentUnit[];
  slots: PhaseEquipmentSlot[];
  type_slots: PhaseEquipmentTypeSlot[];
}

export interface InventoryItem {
  id: string;
  name: string;
  category: string | null;
  asset_tag: string | null;
  quantity: string | null;
  equipment_item_id: string | null;
  equipment_name: string | null;
  notes: string | null;
}

class PipelineProjectService {
  /** Server-computed planned vs actual. `{permitted:false}` on 403 — see the header.
   *
   * `baselineVersion` selects WHICH frozen scope to measure against (P4). Omitted means the
   * latest — "are we on budget". Passing 1 asks the other question: "how does this compare to
   * what we originally bid". The two differ by exactly the approved change orders between them.
   */
  static async getBidVsActual(
    matterid: string, baselineVersion?: number,
  ): Promise<BidVsActual | BidVsActualDenied> {
    try {
      const qs = baselineVersion === undefined ? '' : `?baseline_version=${baselineVersion}`;
      const res = await http.get<any>(`/projects/${matterid}/bid-vs-actual${qs}`);
      return { permitted: true, ...res.data } as BidVsActual;
    } catch (err) {
      if ((err as HttpError).status === 403) return { permitted: false };
      throw err;
    }
  }

  /** The per-day P&L (Tier 4.2). 403 = a normal answer for a blind caller, like bid-vs-actual. */
  static async getPnl(matterid: string): Promise<Pnl | PnlDenied> {
    try {
      const res = await http.get<any>(`/projects/${matterid}/pnl`);
      return { permitted: true, ...res.data } as Pnl;
    } catch (err) {
      if ((err as HttpError).status === 403) return { permitted: false };
      throw err;
    }
  }

  /** The project's frozen phases — progress structure, readable by every role on the matter. */
  static async getPhases(matterid: string): Promise<ProjectPhase[]> {
    const res = await http.get<any>(`/projects/${matterid}/phases`);
    return (res.data?.phases ?? []) as ProjectPhase[];
  }

  /** The project's cost codes. `planned_dollars` is server-stripped for blind callers. */
  static async getCostCodes(matterid: string): Promise<ProjectCostCode[]> {
    const res = await http.get<any>(`/projects/${matterid}/cost-codes`);
    return (res.data?.cost_codes ?? []) as ProjectCostCode[];
  }

  // ── Phase 4: field data ────────────────────────────────────────────────────────────────────────

  /** The project's field tickets. Line rate keys are absent for blind callers. */
  static async getFieldTickets(matterid: string): Promise<FieldTicket[]> {
    const res = await http.get<any>(`/projects/${matterid}/field-tickets`);
    return (res.data?.tickets ?? []) as FieldTicket[];
  }

  /** Metres and hours per phase — money-free by construction, open to every member of the
   * matter (the A6 man-hours card's source; no other frontend consumer existed before it). */
  static async getProduction(matterid: string): Promise<ProductionRow[]> {
    const res = await http.get<any>(`/projects/${matterid}/production`);
    return (res.data?.production ?? []) as ProductionRow[];
  }

  /** Record a day's production. The body carries no money by TYPE — with ONE recorded
   * exception: invoices[].invoice_value, the vendor's own figure (gap 3.4). */
  static async createFieldTicket(
    matterid: string, body: CreateFieldTicketBody,
  ): Promise<{ id: string }> {
    const res = await http.post<any>(`/projects/${matterid}/field-tickets`, body);
    return res.data as { id: string };
  }

  /** Edit-until-verified (residual gap A3): replace an UNVERIFIED day wholesale — the same
   * no-money body as create; the server re-runs every creation wall and refuses a verified
   * day on BOTH the stored and the incoming date. FA tickets have their own replace
   * (fa-ticket-edit closed the recorded carve-out). */
  static async replaceFieldTicket(
    matterid: string, ticketId: string, body: CreateFieldTicketBody,
  ): Promise<{ id: string }> {
    const res = await http.put<any>(`/projects/${matterid}/field-tickets/${ticketId}`, body);
    return res.data as { id: string };
  }

  // ── Phase 4b: commercial ───────────────────────────────────────────────────────────────────────

  /** FA days, open to the matter; the frozen MSA money on the lines is server-stripped. */
  static async getForceAccount(matterid: string): Promise<ForceAccountTicket[]> {
    const res = await http.get<any>(`/projects/${matterid}/force-account`);
    return (res.data?.tickets ?? []) as ForceAccountTicket[];
  }

  /** Record an FA day — hours and a reason; the server prices and decides billability. */
  static async createForceAccount(
    matterid: string,
    body: CreateForceAccountBody,
  ): Promise<{ id: string; billable: boolean }> {
    const res = await http.post<any>(`/projects/${matterid}/force-account`, body);
    return res.data as { id: string; billable: boolean };
  }

  /** Edit-until-verified for FA (fa-ticket-edit, closing the A3 carve-out): replace an
   * UNVERIFIED FA day wholesale — creation's exact body, one form two modes. The server
   * RE-PRICES every line from the CURRENT MSA card (a replace is new use), re-freezes
   * `billable` from the incoming reason, and refuses a verified day on BOTH the stored and
   * the incoming date. The response is the POST's own shape — attribution, no money. */
  static async replaceForceAccount(
    matterid: string, ticketId: string, body: CreateForceAccountBody,
  ): Promise<{ id: string; billable: boolean }> {
    const res = await http.put<any>(`/projects/${matterid}/force-account/${ticketId}`, body);
    return res.data as { id: string; billable: boolean };
  }

  /** Billable vs absorbed. Money end to end, so 403 is a normal answer — `{permitted:false}`. */
  static async getForceAccountSummary(
    matterid: string,
  ): Promise<ForceAccountSummary | BidVsActualDenied> {
    try {
      const res = await http.get<any>(`/projects/${matterid}/force-account/summary`);
      return { permitted: true, ...res.data } as ForceAccountSummary;
    } catch (err) {
      if ((err as HttpError).status === 403) return { permitted: false };
      throw err;
    }
  }

  /** Change orders — a commercial money surface, 403 for blind roles → `{permitted:false}`. */
  static async getChangeOrders(matterid: string): Promise<ChangeOrders | BidVsActualDenied> {
    try {
      const res = await http.get<any>(`/projects/${matterid}/change-orders`);
      return { permitted: true, change_orders: res.data?.change_orders ?? [] } as ChangeOrders;
    } catch (err) {
      if ((err as HttpError).status === 403) return { permitted: false };
      throw err;
    }
  }

  /** The CO's lifecycle trail (gap review 2.16) — same SEE_FINANCIALS gate as the CO list. */
  static async getChangeOrderHistory(
    matterid: string,
    coId: string,
  ): Promise<ChangeOrderHistoryEntry[]> {
    const res = await http.get<any>(`/projects/${matterid}/change-orders/${coId}/history`);
    return res.data?.history ?? [];
  }

  static async createChangeOrder(
    matterid: string,
    body: { title: string; description?: string;
            lines: { description: string; amount: number; included?: boolean }[] },
  ): Promise<{ id: string; status: string }> {
    const res = await http.post<any>(`/projects/${matterid}/change-orders`, body);
    return res.data as { id: string; status: string };
  }

  static async transitionChangeOrder(
    matterid: string, coId: string, status: string,
  ): Promise<{ id: string; status: string }> {
    const res = await http.post<any>(
      `/projects/${matterid}/change-orders/${coId}/transition`, { status });
    return res.data as { id: string; status: string };
  }

  /** Replace a CO's impact lines WHOLESALE (residual gap A2). Lines carry no ids — the server
   * deletes and re-inserts the set, re-minting `sequence` from array order — so callers send the
   * FULL set every time, never a delta. A Closed CO refuses the edit (400). */
  static async replaceChangeOrder(
    matterid: string,
    coId: string,
    body: { lines: { description: string; amount: number; included: boolean }[] },
  ): Promise<{ id: string }> {
    const res = await http.put<any>(`/projects/${matterid}/change-orders/${coId}`, body);
    return res.data as { id: string };
  }

  /** Planned vs actual headcount per phase — money-free by construction, open to the matter. */
  static async getCrewLoading(matterid: string): Promise<CrewLoading> {
    const res = await http.get<any>(`/projects/${matterid}/crew-loading`);
    return res.data as CrewLoading;
  }

  /** The project's signed-off days (gap 2.10) — facts, open to every member of the matter. */
  static async getVerifiedDays(matterid: string): Promise<VerifiedDay[]> {
    const res = await http.get<any>(`/projects/${matterid}/verified-days`);
    return (res.data?.verified_days ?? []) as VerifiedDay[];
  }

  static async verifyDay(matterid: string, day: string): Promise<void> {
    await http.post(`/projects/${matterid}/verified-days`, { day });
  }

  static async unverifyDay(matterid: string, day: string): Promise<void> {
    await http.delete(`/projects/${matterid}/verified-days/${day}`);
  }

  // ── P7b (0050): classification and its consequences ──────────────────────────────────────

  /** Per (person, day) STATUTORY hours — the figure the payroll long sheet must export.
   *
   * Counsel D4, confirmed MANDATORY rather than default: only the statutory figure is exportable,
   * and the legacy figure may be DISPLAYED beside it during the dual-meaning window. It cannot be
   * derived in the browser — a block belongs to a LINE and the statutory answer for a person-day
   * is a sum across that person's lines — which is why this is a route rather than a helper. */
  static async getClassifiedHours(
    matterid: string, start?: string, end?: string,
  ): Promise<ClassifiedHour[]> {
    const query = new URLSearchParams();
    if (start) query.set('start', start);
    if (end) query.set('end', end);
    const suffix = query.toString() ? `?${query}` : '';
    const res = await http.get<any>(`/projects/${matterid}/classified-hours${suffix}`);
    return (res.data?.hours ?? []) as ClassifiedHour[];
  }

  /** The off-baseline queue: `(phase, cost_code)` pairs nobody planned. Open items by default. */
  static async getOffBaseline(
    matterid: string, includeDisposed = false,
  ): Promise<OffBaselineLine[]> {
    const suffix = includeDisposed ? '?include_disposed=true' : '';
    const res = await http.get<any>(`/projects/${matterid}/off-baseline${suffix}`);
    return (res.data?.lines ?? []) as OffBaselineLine[];
  }

  /** The supervisor's WHY. It SURVIVES the disposition — a record of who decided, without the
   * why, is not accountability. */
  static async annotateOffBaseline(
    matterid: string, lineId: string, note: string | null,
  ): Promise<void> {
    await http.put(`/projects/${matterid}/off-baseline/${lineId}/note`, { note });
  }

  /** The PM's decision. Identity and timestamp are written SERVER-SIDE and never supplied. */
  static async disposeOffBaseline(
    matterid: string, lineId: string, disposition: OffBaselineDisposition,
  ): Promise<void> {
    await http.put(
      `/projects/${matterid}/off-baseline/${lineId}/disposition`, { disposition });
  }

  /** The PM's release queue: force-account lines the commercial sweep created and HELD. */
  static async getHeldForceAccount(matterid: string): Promise<TicketLine[]> {
    const res = await http.get<any>(`/projects/${matterid}/force-account/held`);
    return (res.data?.lines ?? []) as TicketLine[];
  }

  /** Release or reject swept hours. It GATES and never rewrites the frozen `billable`: a line the
   * PM will not charge is 'rejected', which withholds it from billing while leaving the
   * derivation intact. 'held' is the sweep's own state and is not something a person sets. */
  static async setForceAccountRelease(
    matterid: string, lineIds: string[], releaseStatus: 'released' | 'rejected',
  ): Promise<void> {
    await http.post(`/projects/${matterid}/force-account/release`,
      { line_ids: lineIds, release_status: releaseStatus });
  }

  /** Field-vs-verified basis disagreement, plus the entered-vs-statutory dimension — which
   * renders EXPLICITLY EMPTY WITH ITS REASON rather than blank, because a dimension that renders
   * nothing reads as the field and the statute AGREEING. */
  static async getReclassificationReport(
    matterid: string, start?: string, end?: string,
  ): Promise<ReclassificationReport> {
    const query = new URLSearchParams();
    if (start) query.set('start', start);
    if (end) query.set('end', end);
    const suffix = query.toString() ? `?${query}` : '';
    const res = await http.get<any>(`/projects/${matterid}/reclassification-report${suffix}`);
    return res.data as ReclassificationReport;
  }

  /** The source bid's inventory, read through the matter. No rates on the table at all. */
  static async getInventory(matterid: string): Promise<InventoryItem[]> {
    const res = await http.get<any>(`/projects/${matterid}/inventory`);
    return (res.data?.items ?? []) as InventoryItem[];
  }

  // ── Tier 1: phase-level crew (the optional layer above the matter team) ─────────────────────────

  /** Who is on each phase and the planned role slots they fill (0026) — RECORD-ONLY and
   * money-free, readable by any member of the matter. */
  static async getPhaseCrew(matterid: string): Promise<PhaseCrewRead> {
    const res = await http.get<any>(`/projects/${matterid}/phase-crew`);
    return {
      assignments: (res.data?.phase_crew ?? []) as PhaseCrewMember[],
      slots: (res.data?.slots ?? []) as PhaseCrewSlot[],
    };
  }

  /** Assign a team member to a phase, optionally into a role slot (MUTATE_BIDS). The assignee
   * must already be on the matter team — the server's subset wall refuses anyone else. */
  static async assignPhaseCrew(
    matterid: string, phaseId: string, employeeId: string, roleKey?: string | null,
  ): Promise<{ id: string }> {
    const res = await http.post<any>(
      `/projects/${matterid}/phases/${phaseId}/crew`,
      { employee_id: employeeId, ...(roleKey ? { role_key: roleKey } : {}) });
    return res.data as { id: string };
  }

  /** Remove a person from a phase SLOT (MUTATE_BIDS), audited server-side. No roleKey targets the
   * role-less Tier-1 row — removing one of a person's two roles never takes the other. */
  static async unassignPhaseCrew(
    matterid: string, phaseId: string, employeeId: string, roleKey?: string | null,
  ): Promise<void> {
    const query = roleKey ? `?role_key=${encodeURIComponent(roleKey)}` : '';
    await http.delete(`/projects/${matterid}/phases/${phaseId}/crew/${employeeId}${query}`);
  }

  // ── Phase equipment (named iron, 0027) — the crew calls' unit-for-person mirror ─────────────────

  /** Which units are on each phase and the planned equipment slots they fill — RECORD-ONLY and
   * money-free, readable by any member of the matter. */
  static async getPhaseEquipment(matterid: string): Promise<PhaseEquipmentRead> {
    const res = await http.get<any>(`/projects/${matterid}/phase-equipment`);
    return {
      assignments: (res.data?.phase_equipment ?? []) as PhaseEquipmentUnit[],
      slots: (res.data?.slots ?? []) as PhaseEquipmentSlot[],
      type_slots: (res.data?.type_slots ?? []) as PhaseEquipmentTypeSlot[],
    };
  }

  /** Assign a fleet unit to a phase, optionally into a slot (MUTATE_BIDS). The unit must be this
   * tenant's, active, asset-tagged and in this project's pool — the server's fleet wall refuses
   * anything else; a type-mismatched unit is accepted (substitution is disclosed, not refused). */
  static async assignPhaseEquipment(
    matterid: string, phaseId: string, inventoryItemId: string, equipmentKey?: string | null,
  ): Promise<{ id: string }> {
    const res = await http.post<any>(
      `/projects/${matterid}/phases/${phaseId}/equipment`,
      { inventory_item_id: inventoryItemId,
        ...(equipmentKey ? { equipment_key: equipmentKey } : {}) });
    return res.data as { id: string };
  }

  /** Remove a unit from a phase SLOT (MUTATE_BIDS), audited server-side. No equipmentKey targets
   * the slot-less row — removing one of a unit's two slots never takes the other. */
  static async unassignPhaseEquipment(
    matterid: string, phaseId: string, inventoryItemId: string, equipmentKey?: string | null,
  ): Promise<void> {
    const query = equipmentKey ? `?equipment_key=${encodeURIComponent(equipmentKey)}` : '';
    await http.delete(
      `/projects/${matterid}/phases/${phaseId}/equipment/${inventoryItemId}${query}`);
  }

  /** Enter (or clear) a phase's ACTUAL dates (0028, MUTATE_BIDS). The resource is the pair —
   * both fields go every call, null = "not entered". The server refuses any other key by name:
   * the planned window is frozen and scope/price changes go through Change Orders. */
  static async updatePhaseSchedule(
    matterid: string, phaseId: string,
    body: { actual_start: string | null; actual_end: string | null },
  ): Promise<{ project_phase_id: string }> {
    const res = await http.put<any>(`/projects/${matterid}/phases/${phaseId}/schedule`, body);
    return res.data as { project_phase_id: string };
  }
}


// ── P7a: the person-day record (0049) ────────────────────────────────────────────────────────

/** One person's one day. Cross-matter by design: a person works two matters on one Tuesday and
 *  still has one person-day, which is why this carries no matterid. */
export interface PersonDay {
  id: string;
  employeeid: string;
  work_date: string;
  status: 'worked' | 'absent';
  absence_reason_id: string | null;
  replaced_by_employeeid: string | null;
  /** Null means day-grain classification on the recorded date — a real answer, not a missing
   *  one, and deliberately never defaulted (A7/D5). */
  shift_start_time: string | null;
  submitted_by: string | null;
}

export interface AbsenceReason {
  id: string;
  code: string;
  label: string;
  sort_order: number;
  active: boolean;
}

/** One stretch of the caller's own day. There is no employee field ANYWHERE in this shape: the
 *  server stamps the caller's identity and offers no override (register F6). There is no
 *  labour_role_id either — the server resolves the role from the caller's own roster
 *  assignment and its schema FORBIDS the field (`extra="forbid"`), so declaring it here was a
 *  latent 400 for any future caller that filled it (review R16). */
export interface MyTimesheetEntry {
  description?: string;
  hours: number;
  hour_type_entered?: 'working' | 'travel';
  project_cost_code_id?: string;
}

export interface PersonDayReadiness {
  timesheet_id: string;
  work_date: string;
  status: string;
  classified_at: string | null;
  blocks: number;
  ready: boolean;
  /** The REASONS it is not ready, named. "Not ready" alone is not actionable. */
  blockers: string[];
}

export interface PersonDayApproval {
  timesheet_id: string;
  work_date: string;
  period_start: string;
  period_end: string;
  period_weeks: number;
  period_source: 'workweek' | 'agreement';
  agreement_id: string | null;
  blocks_written: number;
  force_account_swept: { tickets: number; lines: number };
  daily_overtime_hours: string;
  weekly_overtime_hours: string;
  /** The DISCLOSURE that replaced the withdrawn separation rule: self-approval is allowed, and
   * what is shown is that it happened. */
  self_approved: boolean;
}

export interface ClassifiedBlock {
  line_id: string;
  day: string;
  jurisdiction: string;
  entered_kind: 'working' | 'travel';
  hour_type: 'regular' | 'overtime' | 'double_time';
  hours: string;
  counted: boolean;
  /** Absent for a blind caller — both joined MONEY_KEYS with P7b. */
  multiplier?: string;
  ruleset_id?: string;
  block_ordinal: number;
}

export interface PeriodDelta {
  employeeid: string;
  period_start: string;
  period_end: string;
  period_weeks: number;
  period_source: 'workweek' | 'agreement';
  agreement_id: string | null;
  overtime_movement_hours: string;
  days: { day: string; movement: Record<string, string> }[];
  /** Days with recorded hours and NO approval (R13). Movement above deliberately excludes them —
   * they have no locked snapshot to move against — and a mid-period reader must see the answer
   * is partial rather than read a small delta as a finished period. */
  unapproved_days: string[];
  locked_blocks: number;
  /** Always false, and asserted rather than assumed: the delta is report-only. */
  posted: boolean;
}

export class PersonDayService {
  /** Person-days for a date range. The SERVER narrows to the caller unless they may see
   *  everyone (permish {1,2} or RECORD_PRODUCTION) — the client never asserts scope. The
   *  fan-out surface reads this to show the stored shift start it would otherwise post over
   *  blind (review R1). */
  static async listDays(start: string, end: string): Promise<PersonDay[]> {
    const params = new URLSearchParams({ start, end });
    const res = await http.get<any>(`/api/timesheets?${params.toString()}`);
    return (res.data?.items ?? []) as PersonDay[];
  }

  /** `mine` is resolved SERVER-side — the client never names whose days it wants. */
  static async listMyDays(start?: string, end?: string): Promise<PersonDay[]> {
    const params = new URLSearchParams({ mine: 'true' });
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    const res = await http.get<any>(`/api/timesheets?${params.toString()}`);
    return (res.data?.items ?? []) as PersonDay[];
  }

  // ── P7b (0050): approval, readiness and the period-close delta ───────────────────────────

  /** Is this person-day ready for payroll, and if NOT, why not.
   *
   * DERIVED, never stored: readiness is a projection of the verified-days set, and a stored copy
   * of a computed fact is the drift the schema warns about. It returns the BLOCKERS rather than a
   * boolean, because an approver told "not ready" has nothing to act on. */
  static async getReadiness(employeeid: string, workDate: string): Promise<PersonDayReadiness> {
    const params = new URLSearchParams({ employeeid, work_date: workDate });
    const res = await http.get<any>(`/api/timesheets/readiness?${params.toString()}`);
    return res.data?.item as PersonDayReadiness;
  }

  /** Approve one person-day: classify the period to date, write THAT day's blocks, sweep its
   * force-account hours into a held state. One act, one transaction.
   *
   * SELF-APPROVAL IS ALLOWED and DISCLOSED. Leads enter their own time, so approving a day you
   * authored is ordinary work — the response says when it happened rather than refusing it. */
  static async approvePersonDay(
    employeeid: string, workDate: string,
  ): Promise<PersonDayApproval> {
    const res = await http.post<any>('/api/timesheets/approve',
      { employeeid, work_date: workDate });
    return res.data as PersonDayApproval;
  }

  /** The blocks under one person-day. `multiplier` and `ruleset_id` are stripped for a blind
   * caller; `hour_type` is NOT money and must never be — it IS the classification. */
  static async getClassification(timesheetId: string): Promise<ClassifiedBlock[]> {
    const res = await http.get<any>(`/api/timesheets/${timesheetId}/classification`);
    return (res.data?.blocks ?? []) as ClassifiedBlock[];
  }

  /** The period-close delta: what a re-run today would say, against what was locked.
   *
   * COMPUTED, SHOWN, AND POSTED NOWHERE. The re-run's blocks are never written — persisting them
   * would post the delta by the back door. It gets its OWN surface rather than joining the
   * reclassification report, whose headline is a rate: a rate cannot be computed when its
   * denominator is three different things.
   *
   * NULL IS THE SERVER'S GENUINE "no recorded hours in that period" ANSWER (a 404), and it is a
   * different fact from a failure (R13). Mapping it here is what lets the panel branch on it —
   * under react-query v5 a queryFn resolving `undefined` is itself an error, so without the
   * mapping the "no recorded hours" branch was dead and the 404 rendered as its opposite. */
  static async getPeriodDelta(employeeid: string, day: string): Promise<PeriodDelta | null> {
    const params = new URLSearchParams({ employeeid, day });
    try {
      const res = await http.get<any>(`/api/timesheets/delta?${params.toString()}`);
      return res.data?.item as PeriodDelta;
    } catch (err) {
      if ((err as HttpError).status === 404) return null;
      throw err;
    }
  }

  static async listAbsenceReasons(activeOnly = true): Promise<AbsenceReason[]> {
    const res = await http.get<any>(`/api/absence-reasons?active_only=${activeOnly}`);
    return (res.data?.items ?? []) as AbsenceReason[];
  }

  /** The tenant registry's first write door (P7a). Sunday is 0 — `period_builder.week_start`'s
   *  convention and 0037's CHECK. Gated on permish {1} server-side; the caller must gate on the
   *  same predicate, not on an approximation of it. */
  static async setWorkweekStartDay(day: number): Promise<number> {
    const res = await http.put<any>('/api/config/workweek-start-day',
      { workweek_start_day: day });
    return (res.data?.workweekStartDay ?? day) as number;
  }

  static async createAbsenceReason(body: {
    code: string; label: string; sort_order?: number;
  }): Promise<AbsenceReason> {
    const res = await http.post<any>('/api/absence-reasons', body);
    return res.data?.item as AbsenceReason;
  }

  /** Rename, reorder, retire or reactivate. There is no delete: person-days reference these
   *  rows, and `active = false` is what lets history keep reading (the 0043 precedent). */
  static async updateAbsenceReason(
    id: string, body: { label?: string; sort_order?: number; active?: boolean },
  ): Promise<AbsenceReason> {
    const res = await http.put<any>(`/api/absence-reasons/${id}`, body);
    return res.data?.item as AbsenceReason;
  }

  /** Record the caller's own construction hours, OFF the legal billing spine. */
  static async recordMyDay(body: {
    matterid: string; ticket_date: string; project_phase_id?: string;
    project_cost_code_id?: string; site_id?: string; notes?: string;
    entries: MyTimesheetEntry[];
  }): Promise<{ id: string }> {
    const res = await http.post<any>('/api/my-timesheet/entries', body);
    return res.data?.item as { id: string };
  }

  static async recordAbsence(body: {
    employeeid: string; work_date: string;
    absence_reason_id?: string; replaced_by_employeeid?: string;
  }): Promise<PersonDay> {
    const res = await http.post<any>('/api/timesheets/absences', body);
    return res.data?.item as PersonDay;
  }

  static async clearAbsence(employeeid: string, work_date: string): Promise<void> {
    // HttpService.delete takes the BODY directly (it is not axios.delete) — wrapping it in
    // `{ data: ... }` double-wrapped the payload and 400d every call (review R16).
    await http.delete<any>('/api/timesheets/absences', { employeeid, work_date });
  }

  /** One shift start across a crew's day — the fan-out's companion (41 people, one call). */
  static async setDayShiftStart(
    employee_ids: string[], work_date: string, shift_start_time: string | null,
  ): Promise<number> {
    const res = await http.put<any>('/api/timesheets/shift-start/day',
      { employee_ids, work_date, shift_start_time });
    return (res.data?.updated ?? 0) as number;
  }

  static async setShiftStart(
    employeeid: string, work_date: string, shift_start_time: string | null,
  ): Promise<PersonDay> {
    const res = await http.put<any>('/api/timesheets/shift-start',
      { employeeid, work_date, shift_start_time });
    return res.data?.item as PersonDay;
  }
}

export default PipelineProjectService;
